import React, { useEffect, useRef } from 'react';
import { ambientFromImage, ambientHost } from '../../utils/ambientColor';
import { coverAt } from '../../utils/cover';
import { track } from '../../utils/analytics';
import { useHeroListen, HeroWave, HeroListenPlayButton, EqBars, HeardCheck } from './HeroListen';

// DESIGN V2 — the hero row at the top of For You: the top 3-4 picks as large
// cards lit by their own cover art. The first card is 1.7x wide and carries
// the page's ONE filled button (Play); the others play on click.
//
// items: [{ key, art, kicker, title, meta, onOpen, onPlay, playing, loading, playButton, listen }]
//   playButton — optional node rendered in place of the default Play button
//   (For You tracks wrap it in DJPalAudioPreview so the player bar follows).
//   listen — { trackId, src, previewId, origin } for a track card: the card
//   then shows its listen (live waveform / heard waveform / Resume — see
//   HeroListen.jsx), and `playButton` is expected to wrap a HeroListenPlayButton.
//
// Lights the nearest `.v2-ambient` ancestor from the first card's art.
export function useAmbientFromArt(rootRef, artSrc) {
    useEffect(() => {
        let cancelled = false;
        const el = ambientHost(rootRef.current);
        if (!el) return undefined;
        if (!artSrc) { el.style.removeProperty('--v2-ambient'); el.style.removeProperty('--v2-ambient-2'); return undefined; }
        ambientFromImage(artSrc).then((c) => {
            if (cancelled || !c) return;
            el.style.setProperty('--v2-ambient', c.primary);
            el.style.setProperty('--v2-ambient-2', c.secondary);
        });
        return () => { cancelled = true; };
    }, [rootRef, artSrc]);
}

export default function DiscoverHero({ items, className = '', origin = 'feed' }) {
    const rootRef = useRef(null);
    useAmbientFromArt(rootRef, items[0]?.art || null);
    // On a phone the row is a sideways snap strip (design-v2/_mobile.scss);
    // one event per mount the first time it is swiped — the signal for
    // whether anyone sees past the lead card. Desktop's grid never scrolls.
    const swipedRef = useRef(false);
    useEffect(() => { swipedRef.current = false; }, [origin]);
    const onScroll = (e) => {
        if (swipedRef.current || e.currentTarget.scrollLeft < 24) return;
        swipedRef.current = true;
        track('feed_hero_swiped', { origin, count: items.length });
    };
    if (!items.length) return null;
    return (
        <div className={`v2-hero-row ${className}`} ref={rootRef} data-count={items.length} onScroll={onScroll}>
            {items.map((it, i) => <HeroCard key={it.key ?? i} it={it} lead={i === 0} />)}
        </div>
    );
}

function HeroCard({ it, lead }) {
    const listen = useHeroListen(it.listen);
    const playing = it.playing || (listen.active && listen.playing);
    const kicker = listen.active && listen.isCurrent && listen.playing
        ? <><EqBars /><span>Now playing</span></>
        : (listen.done ? <><HeardCheck /><span>{it.kicker}</span></> : <span>{it.kicker}</span>);
    return (
        <div
            className={`v2-hero-card${lead ? ' lead' : ''}${playing ? ' playing' : ''}${listen.active && listen.showWave ? ' has-wave' : ''}${listen.active && listen.heard && !listen.isCurrent ? ' heard' : ''}`}
            style={it.art ? { '--v2-hero-art': `url("${coverAt(it.art, lead ? 800 : 400)}")` } : undefined}
            onClick={it.onOpen}
            role={it.onOpen ? 'button' : undefined}
        >
            <div className="v2-hero-card-body">
                {it.kicker && <div className="v2-hero-card-kicker">{kicker}</div>}
                <div className="v2-hero-card-title" title={typeof it.title === 'string' ? it.title : undefined}>{it.title}</div>
                {it.meta && <div className="v2-hero-card-meta">{it.meta}</div>}
                {listen.active && listen.showWave && <HeroWave state={listen} />}
                {lead && (
                    <div className="v2-hero-card-actions" onClick={(e) => e.stopPropagation()}>
                        {it.playButton || (
                            <button type="button" className="v2-btn primary" onClick={it.onPlay} disabled={!it.onPlay || it.loading}>
                                {it.playing ? <span>Pause</span> : <><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="7 4 20 12 7 20 7 4" /></svg><span>Play</span></>}
                            </button>
                        )}
                        {it.secondary}
                    </div>
                )}
            </div>
            {!lead && (it.onPlay || it.playButton) && (
                <div className="v2-hero-card-play" onClick={(e) => e.stopPropagation()}>
                    {it.playButton || (
                        <button type="button" className="v2-btn icon" onClick={it.onPlay} disabled={it.loading} title={it.playing ? 'Pause' : 'Play'}>
                            {it.playing
                                ? <svg viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="3" width="5" height="18" rx="1" /><rect x="14" y="3" width="5" height="18" rx="1" /></svg>
                                : <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="7 4 20 12 7 20 7 4" /></svg>}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export { HeroListenPlayButton };
